/**
 * Custom hook to handle borrower organization submission
 * Consolidates create and update org mutation logic with shareholder invitation
 */

import { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  KYCStatus,
  Persona,
} from "@funded-here-interface/common/src/constant/enum";
import { countryNameToCodeMap } from "@funded-here-interface/common/src/constant/country";
import { ThanksPageType } from "../../../constants/enums";
import { navigateToThanksPage } from "../../ThanksPage/ThanksPageUtils";
import useCreateOrg from "../../../hooks/useCreateOrg";
import useUpdateOrg from "../../../hooks/useUpdateOrg";
import useInviteUserToOrg from "../../../hooks/useInviteUserToOrg";
import {
  buildBorrowerBankObject,
  buildCreateBorrowerOrgData,
  buildUpdateBorrowerOrgData,
  BorrowerBizState,
  BorrowerRepState,
  BorrowerBankState,
} from "../utils/borrowerOrgDataBuilder";
import { DocumentsUpload } from "@funded-here-interface/common/src/constant/type";

interface UseBorrowerOrgSubmissionParams {
  setLoading: (loading: boolean) => void;
  onError: (error: Error) => void;
}

/**
 * Hook to handle borrower organization submission (create or update)
 */
export const useBorrowerOrgSubmission = ({
  setLoading,
  onError,
}: UseBorrowerOrgSubmissionParams) => {
  const navigate = useNavigate();
  const createOrgMutation = useCreateOrg();
  const updateOrgMutation = useUpdateOrg();
  const inviteUserMutation = useInviteUserToOrg();

  /**
   * Invite shareholders/directors to organization
   */
  const inviteShareholder = useCallback(
    (onBoardingUserType: Persona, orgId: number, token: string) => {
      const isSpvOrg = false;
      inviteUserMutation.mutate(
        {
          token,
          orgId,
          onBoardingUserType,
          isSpvOrg,
        },
        {
          onSuccess: () => {
            setLoading(false);
          },
          onError: (e) => {
            setLoading(false);
            onError(e as Error);
          },
        }
      );
    },
    [inviteUserMutation, setLoading, onError]
  );

  /**
   * Submit borrower organization (create or update based on KYC status)
   */
  const submitBorrowerOrganization = useCallback(
    ({
      kycStatus,
      orgId,
      token,
      bizState,
      repState,
      bankState,
      documentsUpload,
    }: {
      kycStatus: string | null;
      orgId: string | null;
      token: string;
      bizState: BorrowerBizState;
      repState: BorrowerRepState;
      bankState: BorrowerBankState;
      documentsUpload: DocumentsUpload;
    }) => {
      // Build bank object
      const bank = buildBorrowerBankObject(bankState);

      // Get country code
      let countryCode: string | null = null;
      if (bizState.countryOfDomicile) {
        countryCode =
          countryNameToCodeMap.get(bizState.countryOfDomicile) || null;
      }

      setLoading(true);

      const baseParams = {
        bizState,
        repState,
        bank,
        token,
        countryCode,
        documentsUpload,
      };

      // If KYC is pending resubmission, update existing org
      if (kycStatus === KYCStatus.PENDING_RESUBMISSION) {
        const updateData = buildUpdateBorrowerOrgData(baseParams);

        updateOrgMutation.mutate(
          {
            id: orgId?.toString() || "",
            data: updateData,
          },
          {
            onSuccess: (data) => {
              setLoading(false);
              inviteShareholder(Persona.MERCHANT, Number(data.id), token);
              navigateToThanksPage(navigate, ThanksPageType.RESUBMITTED);
            },
            onError: (e) => {
              setLoading(false);
              onError(e as Error);
            },
          }
        );
      }
      // Otherwise, create new org
      else {
        const createData = buildCreateBorrowerOrgData(baseParams);

        createOrgMutation.mutate(createData, {
          onSuccess: (data) => {
            setLoading(false);
            inviteShareholder(Persona.MERCHANT, Number(data.id), token);
            navigateToThanksPage(navigate, ThanksPageType.ONBOARDING);
          },
          onError: (e) => {
            setLoading(false);
            onError(e as Error);
          },
        });
      }
    },
    [
      navigate,
      createOrgMutation,
      updateOrgMutation,
      setLoading,
      onError,
      inviteShareholder,
    ]
  );

  return { submitBorrowerOrganization };
};
